import { useMsal } from '@azure/msal-react';
import { Button, Spinner, Text, Tooltip } from '@fluentui/react-components';
import { TrophyFilled } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { JSX } from 'react';
import { NavLink } from 'react-router';
import { useStores } from '../../../common/hooks';
import { AwardedNomination } from '../../../models/index';
import { RoutePaths } from '../../../route-paths';
import { FlexBox } from '../../shared-components/flex-box';
import './AwardedNominationTile.scss';

interface AwardedNominationTileProps {
  awardedNomination: AwardedNomination;
}

const AwardedNominationTileImpl: React.FC<AwardedNominationTileProps> = (props) => {
  const {
    awardedNomination,
  } = props;

  const {
    apiStore,
    awardStore,
    nominationDashboardPageUiStore,
  } = useStores();

  const {
    instance,
  } = useMsal();

  const isActionDisabled = !awardStore.isWinnersWindowOpen || !awardedNomination.canSubmitQuestionnaire;

  const getButtonLabel = (): string => {
    if (awardedNomination.questionnaireStatus === 'Submitted') return 'Update response';

    return 'Accept award';
  };

  const getQuestionnaireRoute = (): string => {
    if (awardedNomination.questionnaireId > 0) {
      return `${RoutePaths.Questionnaires}/${awardedNomination.questionnaireId}`;
    }

    return `${RoutePaths.Winners}/${awardedNomination.winnerId}/questionnaire`;
  };

  const getDisplayMessage = (): JSX.Element | undefined => {
    if (!awardStore.isWinnersWindowOpen) return <Text size={200} as='p'>The Winners Window is closed.</Text>;
    const isLoading = nominationDashboardPageUiStore.isLoadingNominationPermissions.get(awardedNomination.nominationId);

    const permissions = nominationDashboardPageUiStore.nominationPermissions.get(awardedNomination.nominationId);
    if (permissions) {
      return <>
        <Text size={200} as='p'>
          Only the nomination owner or a designated contact can accept this award.
        </Text>
        <Text size={200} as='p'>
          Nomination owner:
          <Text size={200} weight='semibold'>{` ${permissions.nominationOwnerEmailAddress}`}</Text>
        </Text>
      </>
    }

    if (isLoading === false) {
      return <Text size={200} as='p'>The nomination owner could not be loaded. Close and reopen this tooltip to try again.</Text>;
    }

    return undefined;
  };

  const handleTooltipOpen = async (nominationId: number): Promise<void> => {
    if (!awardStore.isWinnersWindowOpen ||
      nominationDashboardPageUiStore.hasNominationPermission(nominationId) ||
      nominationDashboardPageUiStore.isLoadingNominationPermission(nominationId)) return;

    nominationDashboardPageUiStore.setIsLoadingNominationPermissions(nominationId, true);

    try {
      const permissions = await apiStore.getNominationPermissions(instance, nominationId);
      nominationDashboardPageUiStore.setNominationPermissions(nominationId, permissions);
    } catch (error) {
      // TODO: Handle error
    } finally {
      nominationDashboardPageUiStore.setIsLoadingNominationPermissions(nominationId, false);
    }
  };

  return (
    <div className='awarded-nomination-tile'>
      <FlexBox
        aligns='end'
        className='awarded-nomination-tile__title'
      >
        <TrophyFilled fontSize={35} />
        <Text
          size={500}
          weight='semibold'
          block
        >
          Congrats, {awardedNomination.role}!
        </Text>
      </FlexBox>
      <Text
        size={400}
        weight='semibold'
        className='awarded-nomination-tile__award'
        block
      >
        {awardedNomination.awardTitle}
      </Text>
      <Text
        className='awarded-nomination-tile__nomination'
        size={200}
        block
      >
        {awardedNomination.nominationName}
      </Text>
      {
        isActionDisabled ?
          <Tooltip
            content={
              <FlexBox
                className='awarded-nomination-tile__tooltip'
                content='center'
                direction='column'
              >
                {getDisplayMessage()}
                {
                  nominationDashboardPageUiStore.isLoadingNominationPermissions.get(awardedNomination.nominationId) && (
                    <FlexBox
                      className='tooltip-spinner'
                      content='center'
                      aligns='center'
                    >
                      <Spinner aria-label='loading message' size='extra-tiny' />
                    </FlexBox>
                  )
                }
              </FlexBox>
            }
            relationship='description'
            positioning='above-start'
            withArrow
          >
            <Button
              appearance='secondary'
              className='awarded-nomination-tile__action'
              onMouseEnter={() => handleTooltipOpen(awardedNomination.nominationId)}
              onFocus={() => handleTooltipOpen(awardedNomination.nominationId)}
              disabledFocusable
            >
              {getButtonLabel()}
            </Button>
          </Tooltip> :
          <NavLink
            className='awarded-nomination-tile__action'
            to={getQuestionnaireRoute()}
            end
          >
            {getButtonLabel()}
          </NavLink>
      }
    </div>
  );
}

export const AwardedNominationTile = observer(AwardedNominationTileImpl);
